import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Stack,
    Typography,
    Paper,
    Chip,
    Fade,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    Link as LinkIcon,
    CheckCircle as CheckCircleIcon,
    HourglassEmpty as PendingIcon,
    Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { formatWhen } from './utils';

interface LinkDetailsTabProps {
    linkItems: any[];
    onNavigateToResults?: (candidateId: string) => void;
}

export default function LinkDetailsTab({ linkItems, onNavigateToResults }: LinkDetailsTabProps) {
    const totalLinks = linkItems.length;
    const activeSessions = linkItems.filter(item => (item.lastStartAtMs || item.count > 0) && !item.interviewResults).length;
    const completedSessions = linkItems.filter(item => item.interviewResults || item.status === 'completed').length;

    const statCards = [
        {
            title: 'Total Links',
            value: totalLinks,
            color: '#3b82f6',
            bgColor: alpha('#3b82f6', 0.1),
            icon: <LinkIcon />
        },
        {
            title: 'Active Sessions',
            value: activeSessions,
            color: '#10b981',
            bgColor: alpha('#10b981', 0.1),
            icon: <PendingIcon />
        },
        {
            title: 'Completed',
            value: completedSessions,
            color: '#764ba2',
            bgColor: alpha('#764ba2', 0.1),
            icon: <CheckCircleIcon />
        },
    ];

    return (
        <Fade in timeout={400}>
            <Box>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    {statCards.map((stat, index) => (
                        <Grid item xs={12} sm={4} key={index}>
                            <Card sx={{
                                height: '100%',
                                background: stat.bgColor,
                                border: `1px solid ${alpha(stat.color, 0.2)}`,
                            }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                {stat.title}
                                            </Typography>
                                            <Typography variant="h3" sx={{ color: stat.color, fontWeight: 700 }}>
                                                {stat.value}
                                            </Typography>
                                        </Box>
                                        <Box sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2,
                                            bgcolor: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: stat.color,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                        }}>
                                            {stat.icon}
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                <Card>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                            <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                            Recent Activity
                        </Typography>

                        {linkItems.length === 0 ? (
                            <Box sx={{
                                textAlign: 'center',
                                py: 6,
                                color: 'text.secondary'
                            }}>
                                <AssignmentIcon sx={{ fontSize: 48, mb: 2, opacity: 0.4 }} />
                                <Typography>No interview links created yet.</Typography>
                            </Box>
                        ) : (
                            <Stack spacing={1.5}>
                                {linkItems.slice(0, 10).map((item, i) => (
                                    <Paper
                                        key={item.candidateId || item.token || i}
                                        variant="outlined"
                                        onClick={() => {
                                            if ((item.interviewResults || item.status === 'completed') && onNavigateToResults) {
                                                onNavigateToResults(item.candidateId || item.token);
                                            }
                                        }}
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: 1,
                                            transition: 'all 0.2s',
                                            cursor: (item.interviewResults || item.status === 'completed') ? 'pointer' : 'default',
                                            '&:hover': {
                                                bgcolor: 'action.hover',
                                                borderColor: 'primary.main',
                                            },
                                        }}
                                    >
                                        <Box>
                                            <Typography fontWeight={600}>
                                                {item.candidateName || 'Unknown'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {item.candidateEmail || `Token: ${item.candidateId || item.token}`}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            {item.interviewResults || item.status === 'completed' ? (
                                                <Chip
                                                    label="Completed"
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                />
                                            ) : item.lastStartAtMs || item.count > 0 ? (
                                                <Chip
                                                    label="In Progress"
                                                    size="small"
                                                    color="warning"
                                                    variant="outlined"
                                                />
                                            ) : (
                                                <Chip
                                                    label="Pending"
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            )}
                                            <Typography variant="caption" color="text.secondary">
                                                {formatWhen(item) || 'No activity'}
                                            </Typography>
                                        </Stack>
                                    </Paper>
                                ))}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Fade>
    );
}
